import { useState } from 'react'
import { Link, useLocation } from 'react-router'
import { useCopy, useLocale } from '../../content'
import type { ServiceKey } from '../../content/types'
import { SERVICE_KEYS, SERVICE_MEDIA_ROLE } from '../../data/services'
import { matchRoute, normalizePath, pathFor } from '../../routes'
import { Credit } from '../media/Credit'
import { Picture } from '../media/Picture'

function ArrowIcon({ shown }: { shown: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      className={`h-4 w-4 shrink-0 transition-[opacity,transform] duration-200 motion-reduce:transition-none ${
        shown ? 'translate-x-0 opacity-100' : '-translate-x-1 opacity-0'
      }`}
    >
      <path d="M3 8h9M8.5 4.5 12 8l-3.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

/**
 * The desktop «Servizi» dropdown panel: the eight services on the left, and on
 * the right a preview of the one under the pointer or keyboard focus — its
 * poster frame, its one-line summary (`pages[k].short`) and the photo credit
 * (none here: every photo is own work, so `Credit` renders nothing).
 *
 * - The eight previews are stacked in one grid cell and cross-fade, so the
 *   panel keeps the height of the tallest and never jumps. Inactive ones are
 *   `invisible` (out of the tab order, their credit links included).
 * - Each service link is described by its summary (`aria-describedby` works on
 *   the invisible ones too), so a screen reader hears what the preview shows.
 * - Photos are not in the markup until `primed` (the panel was first opened or
 *   hovered): the prerendered pages carry no dropdown images, and the first
 *   open loads the eight 640 px variants once, so later swaps are instant.
 *
 * Hydration: the first render depends only on the pathname (the active row is
 * the current service page, else the first service) and on `primed`, which the
 * header starts `false`.
 */
export function ServicesMenu({
  primed,
  onNavigate,
  currentLinkClass,
}: {
  primed: boolean
  onNavigate: () => void
  currentLinkClass: string
}) {
  const copy = useCopy()
  const locale = useLocale()
  const { pathname } = useLocation()
  const here = normalizePath(pathname)
  const pageService = matchRoute(pathname)?.route.serviceKey ?? SERVICE_KEYS[0]

  const [active, setActive] = useState<ServiceKey>(pageService)
  // A new page re-centres the preview on it — adjusted during render, like the header's menus.
  const [seenPath, setSeenPath] = useState(pathname)
  if (seenPath !== pathname) {
    setSeenPath(pathname)
    setActive(pageService)
  }

  return (
    <div
      id="vc2-services"
      className="grid w-[40rem] grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3 whitespace-normal rounded-2xl border border-vc2-hairline bg-vc2-surface p-3 text-vc2-ink shadow-xl shadow-black/5"
    >
      <ul className="flex flex-col gap-0.5">
        {SERVICE_KEYS.map((k) => {
          const to = pathFor(k, locale)
          const on = k === active
          return (
            <li key={k}>
              <Link
                to={to}
                aria-current={here === to ? 'page' : undefined}
                aria-describedby={`vc2-services-${k}`}
                onPointerEnter={() => setActive(k)}
                onFocus={() => setActive(k)}
                onClick={onNavigate}
                className={`vc2-display flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-base transition-colors ${
                  on ? 'bg-vc2-paper text-vc2-ink' : 'text-vc2-ink-soft'
                } ${currentLinkClass}`}
              >
                <span>{copy.pages[k].name}</span>
                <ArrowIcon shown={on} />
              </Link>
            </li>
          )
        })}
      </ul>

      <div className="grid">
        {SERVICE_KEYS.map((k) => {
          const on = k === active
          const role = SERVICE_MEDIA_ROLE[k]
          return (
            <div
              key={k}
              className={`flex flex-col gap-3 transition-[opacity,visibility] duration-300 [grid-area:1/1] motion-reduce:transition-none ${
                on ? 'visible opacity-100' : 'invisible opacity-0'
              }`}
            >
              <div className="relative aspect-[4/3] overflow-hidden rounded-xl bg-vc2-surface">
                {primed && <Picture role={role} alt="" sizes="304px" fill className="absolute inset-0" />}
              </div>
              <p id={`vc2-services-${k}`} className="text-sm leading-relaxed text-vc2-ink-soft">
                {copy.pages[k].short}
              </p>
              <Credit role={role} />
            </div>
          )
        })}
      </div>
    </div>
  )
}
